import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Tooltip,
  useTheme,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Logout,
  Brightness4,
  Brightness7,
  Home,
  Explore,
  Group,
  RssFeed,
  Info,
  School,
  Settings,
} from "@mui/icons-material";
import axios from "axios";
import { useColorMode } from "../../../theme/ThemeContext";
import { useAuth } from "../../../utils/AuthContext";

// Logos
import kaasLogo from "../Images/Vetrina _Logo.png";
import kaasNightLogo from "../Images/KaasNight.png";

const Header = () => {
  const { mode, toggleColorMode } = useColorMode();
  const { userData, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const API_URL = "https://tdtlworld.com/dss-kaas-backend/";
  const token = localStorage.getItem("jwttoken");

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API_URL}socialapp/notifications/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications(response.data.filter((n) => n.type === "message"));
      } catch (err) {
        console.error("Error fetching notifications", err);
      }
    };

    if (token) {
      fetchNotifications();
    }
  }, [token, API_URL]);

  const userDisplayName = userData
    ? `${userData.first_name} ${userData.last_name}`
    : localStorage.getItem("email") || "User";

  const userProfilePic = userData?.profile_pic || "";

  const isAdminArea = location.pathname.includes("/db-shiksha/kaas/admin");
  const basePrefix = isAdminArea
    ? "/db-shiksha/kaas/admin"
    : "/db-shiksha/kaas/student";

  // Role-based logo redirection
  const userRole = localStorage.getItem("role");
  const logoRedirectPath =
    userRole === "Admin" || userRole === "Instructor"
      ? "/db-shiksha/kaas/admin/dashboard"
      : "/db-shiksha/kaas/student/home";

  const navItems = [
    { name: "Home", path: `${basePrefix}/home`, icon: <Home /> },
    { name: "Explore", path: `${basePrefix}/coursePage`, icon: <Explore /> },
    { name: "My Courses", path: `${basePrefix}/courses`, icon: <School /> },
    { name: "Groups", path: `${basePrefix}/group`, icon: <Group /> },
    { name: "Feeds", path: `${basePrefix}/feeds`, icon: <RssFeed /> },
    { name: "About", path: `${basePrefix}/about`, icon: <Info /> },
  ];

  const drawer = (
    <Box sx={{ width: 280, height: "100%", bgcolor: "background.default" }}>
      <Box
        sx={{ p: 3, textAlign: "center" }}
        component={Link}
        to={logoRedirectPath}
        onClick={() => setMobileOpen(false)}
      >
        <img
          src={mode === "dark" ? kaasNightLogo : kaasLogo}
          alt="Logo"
          style={{ height: "40px", cursor: "pointer" }}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.name}
            component={Link}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: "0 50px 50px 0",
              mr: 2,
              mb: 1,
              bgcolor:
                location.pathname === item.path
                  ? "rgba(212, 175, 55, 0.1)"
                  : "transparent",
              "&:hover": { bgcolor: "rgba(212, 175, 55, 0.05)" },
            }}
          >
            <ListItemIcon
              sx={{
                color: location.pathname === item.path ? "#D4AF37" : "inherit",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 700 : 500,
                color: location.pathname === item.path ? "#D4AF37" : "inherit",
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { lg: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component={Link}
            to={logoRedirectPath}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <img
              src={mode === "dark" ? kaasNightLogo : kaasLogo}
              alt="Logo"
              style={{
                height: "35px",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        {/* Desktop Nav removed as per request */}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Toggle light/dark theme">
            <IconButton onClick={toggleColorMode} color="inherit">
              {mode === "dark" ? (
                <Brightness7 sx={{ color: "#D4AF37" }} />
              ) : (
                <Brightness4 />
              )}
            </IconButton>
          </Tooltip>

          <IconButton
            color="inherit"
            component={Link}
            to={`${basePrefix}/lmsMessage`}
          >
            <Badge badgeContent={notifications.length} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          <Box sx={{ ml: 1 }}>
            <Tooltip title="Open profile">
              <IconButton
                onClick={(e) => setAnchorElUser(e.currentTarget)}
                sx={{ p: 0 }}
              >
                <Avatar
                  alt={userDisplayName}
                  src={userProfilePic}
                  sx={{ border: "2px solid #D4AF37" }}
                />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={() => setAnchorElUser(null)}
              PaperProps={{
                sx: {
                  borderRadius: "12px",
                  minWidth: "200px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {userDisplayName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {localStorage.getItem("role") || "Student"}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchorElUser(null);
                  navigate(`${basePrefix}/profile`);
                }}
              >
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorElUser(null);
                  navigate(`${basePrefix}/settings`);
                }}
              >
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  logout();
                  navigate("/db-shiksha/kaas/login");
                }}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: "block", lg: "none" } }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Header;
